import {
  Box,
  Card,
  CircularProgress,
  Container,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import Flx from "../../../components/layout/Flx";
import Htag from "../../../components/typography/Htag";
import Txt from "../../../components/typography/Txt";
import { isNil } from "lodash";
import {
  CloseFullscreenOutlined,
  OpenInFullOutlined,
} from "@mui/icons-material";
import { grey } from "@mui/material/colors";

const PrimaryContentCard = ({
  children,
  title,
  subTitle,
  endContent,
  loading = false,
  expandedable = false,
  hideTitle = false,
  maxWidth,
  // When true, opt out of the laptop-and-smaller full-width behavior.
  // Default (false) drops the outer MUI Container on viewports below xl
  // (~1536px) so the card fills the dashboard main area without gutters
  // on laptops; wider desktops still get the maxWidth cap.
  disableLaptopFullWidth = false,
  sx,
  cardSx,
  headerSx,
  containerSx,
  titleEndContent,
  bodySx,
  titleSx,
}) => {
  // "Laptop or smaller" = anything under 1600px wide. MUI's built-in
  // breakpoints don't have a 1600 stop (xl = 1536), so use a raw query.
  // The 0.05 avoids a 1px gap if anyone else queries `(min-width:1600px)`.
  const isLaptopOrSmaller = useMediaQuery("(max-width:1599.95px)");
  const useFullWidthOnLaptop = isLaptopOrSmaller && !disableLaptopFullWidth;

  const [expanded, setExpanded] = useState(false);
  const rootStyles = useMemo(() => {
    return {
      py: 4,
      ...sx,
      ...containerSx,
    };
  }, [sx, containerSx]);

  const cardStyles = useMemo(() => {
    if (isLaptopOrSmaller) {
      return {
        borderRadius: 0,
        boxShadow: "none",
        ...cardSx,
      };
    }
    return { ...cardSx };
  }, [cardSx, isLaptopOrSmaller]);
  const cardContent = (
    <CardPaper sx={cardStyles}>
      {/* <Flx column gap={2} sx={{ p: 2 }}> */}
      <SectionHeader
        title={title}
        titleEndContent={titleEndContent}
        subTitle={subTitle}
        hideTitle={hideTitle}
        expanded={expanded}
        titleSx={titleSx}
        expandedable={expandedable}
        endContent={endContent}
        onExpandClick={() => setExpanded(!expanded)}
        sx={headerSx}
      />

      {loading ? (
        <Flx center>
          <CircularProgress size={24} sx={{ my: 8 }} />
        </Flx>
      ) : (
        <Box sx={bodySx}>{children}</Box>
      )}
      {/* </Flx> */}
    </CardPaper>
  );

  // Skip the outer Container (full-width) when:
  //   - no maxWidth was supplied (caller wants raw content), OR
  //   - caller is on laptop-and-smaller and hasn't opted out of the
  //     default full-width-on-laptop behavior.
  if (maxWidth && !useFullWidthOnLaptop) {
    return (
      <Container maxWidth={expanded ? false : maxWidth} sx={rootStyles}>
        {cardContent}
      </Container>
    );
  }
  return cardContent;
};

const CardPaper = ({ children, sx }) => {
  const rootStyles = useMemo(() => {
    return {
      overflow: "hidden",
      boxShadow:
        "rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.1) 0px 4px 6px -4px",
      mb: 4,
      p: 4,
      display: "flex",
      flexDirection: "column",
      ...sx,
    };
  }, [sx]);
  return <Card sx={rootStyles}>{children}</Card>;
};

export const SectionHeader = ({
  title,
  subTitle,
  sx,
  titleSx,
  titleEndContent,
  hideTitle,
  endContent,
  expanded,
  expandedable,
  onExpandClick,
}) => {
  const rootStyles = useMemo(() => {
    return {
      mb: 2,

      ...sx,
    };
  }, [sx]);
  if (isNil(title)) return null;
  return (
    <Flx wrap gap={2} fw jb ac sx={rootStyles}>
      <Flx column gap={0.5}>
        {hideTitle ? null : (
          <Flx ac g={1}>
            <Htag sx={titleSx}>{title}</Htag>
            {titleEndContent}
          </Flx>
        )}
        {subTitle && <Txt secondary>{subTitle}</Txt>}
      </Flx>
      <Flx ac>
        {!expandedable ? null : expanded ? (
          <Tooltip title="Collapse">
            <IconButton
              onClick={onExpandClick}
              sx={{ background: grey[200] }}
              size="small"
            >
              <CloseFullscreenOutlined className="thin" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Expand">
            <IconButton onClick={onExpandClick} size="small">
              <OpenInFullOutlined className="thin" />
            </IconButton>
          </Tooltip>
        )}
        {endContent && <div>{endContent}</div>}
      </Flx>
    </Flx>
  );
};
export default PrimaryContentCard;
